import { useMemo, useState } from "react";

import Button from "@/components/Button";
import { US_STATE_OPTIONS } from "./careerIntentCatalog";
import CareerGoalsRankedSelection from "./_CareerGoalsRankedSelection";
import {
  moveRankedSelection,
  RANKED_LOCATION_HINT,
  rankedSelectionIndex,
  toggleRankedSelection
} from "./careerIntentForm";

function selectedStatesLabel(count: number) {
  return count > 0 ? `${count} state${count === 1 ? "" : "s"} selected` : "Select states…";
}

export default function CareerIntentStatePicker({
  disabled = false,
  onChange,
  value
}: {
  disabled?: boolean;
  onChange: (value: string[]) => void;
  value: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const atMaxSelections = value.length >= 3;
  const summary = selectedStatesLabel(value.length);

  const visibleStates = useMemo(
    () =>
      US_STATE_OPTIONS.filter(
        (option) =>
          !normalizedSearch ||
          option.label.toLowerCase().includes(normalizedSearch) ||
          option.value.toLowerCase().includes(normalizedSearch)
      ),
    [normalizedSearch]
  );

  const toggleState = (stateCode: string) => {
    if (disabled) return;
    onChange(toggleRankedSelection(value, stateCode));
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-body-small text-sys-brand-on-surface-variant">{RANKED_LOCATION_HINT}</p>
      {value.length > 0 ? (
        <div className="flex flex-row flex-wrap gap-2">
          {value.map((stateCode) => {
            const state = US_STATE_OPTIONS.find((option) => option.value === stateCode);
            if (!state) return null;
            const rank = rankedSelectionIndex(value, state.value);

            return (
              <button
                type="button"
                aria-checked={true}
                aria-label={`Remove ${state.label}`}
                className="rounded-full border border-sys-brand-outline bg-extended-pgasilver-brand-color px-4 py-3 text-title-medium-bold text-extended-pgasilver-brand-on-color"
                data-testid={`career-intent-state-chip-${state.value}`}
                disabled={disabled}
                key={state.value}
                onClick={() => toggleState(state.value)}
                role="checkbox"
              >
                {rank != null ? `${rank}. ` : ""}
                {state.label} ×
              </button>
            );
          })}
        </div>
      ) : null}

      <CareerGoalsRankedSelection
        disabled={disabled}
        items={value.flatMap((stateCode) => {
          const state = US_STATE_OPTIONS.find((option) => option.value === stateCode);
          return state ? [{ value: state.value, label: state.label }] : [];
        })}
        onMove={(index, direction) => onChange(moveRankedSelection(value, index, direction))}
        testIDPrefix="career-intent-state"
      />

      <div
        className="rounded-lg border border-sys-brand-outline bg-sys-brand-surface"
        data-testid="career-intent-state-picker"
      >
        {isOpen ? (
          <>
            <div className="flex min-h-12 w-full items-center justify-between px-4 py-3">
              <span className="flex-1 text-body-large text-sys-brand-on-surface">{summary}</span>
              <Button
                type="button"
                ariaLabel="Close state picker"
                variant="text"
                testId="career-intent-state-picker-close"
                onClick={() => setIsOpen(false)}
              >
                Done
              </Button>
            </div>
            <div className="px-3 pb-3">
              <input
                aria-label="Search states"
                className="mb-3 min-h-11 w-full rounded-md border border-sys-brand-outline bg-sys-brand-surface px-3 py-2 text-body-large text-sys-brand-on-surface"
                data-testid="career-intent-state-search"
                disabled={disabled}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search states"
                value={search}
              />
              <div role="list">
                {visibleStates.map((state) => {
                  const selected = value.includes(state.value);
                  const optionDisabled = disabled || (!selected && atMaxSelections);

                  return (
                    <button
                      type="button"
                      aria-checked={selected}
                      className={`mb-1 flex min-h-11 w-full items-center rounded-md px-3 py-2 text-left ${
                        selected ? "bg-extended-pgasilver-brand-color" : ""
                      }${optionDisabled ? " opacity-50" : ""}`}
                      data-testid={`career-intent-state-option-${state.value}`}
                      disabled={optionDisabled}
                      key={state.value}
                      onClick={() => toggleState(state.value)}
                      role="checkbox"
                    >
                      <span className="mr-3 w-6 text-center text-title-medium-bold text-extended-pgasilver-brand-on-color">
                        {selected ? rankedSelectionIndex(value, state.value) : ""}
                      </span>
                      <span className="text-body-large text-sys-brand-on-surface">{state.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <button
            type="button"
            aria-expanded={false}
            aria-label="Choose states"
            className="flex min-h-12 w-full items-center justify-between px-4 py-3 text-left"
            data-testid="career-intent-state-picker-toggle"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          >
            <span className="flex-1 text-body-large text-sys-brand-on-surface">{summary}</span>
            <span className="text-title-medium text-sys-brand-on-surface-variant">+</span>
          </button>
        )}
      </div>
    </div>
  );
}
